import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useState } from 'react';
import Markdown from 'react-markdown';

import './styles.css';
import type { Props } from './types';

const AnimatedEllipsis = () => (
  <span aria-hidden="true">
    <span className="context-dot">.</span>
    <span className="context-dot">.</span>
    <span className="context-dot">.</span>
  </span>
);

const formatKey = (key: string) => key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

interface Step {
  label: string;
  content?: string;
  contentLabel?: string;
}

const buildSteps = (item: Props['item']): Step[] => {
  const steps: Step[] = [];

  // context_prompt arrives first from the backend (the vectorization query)
  if (item.context_prompt) {
    steps.push({
      label: 'Analyzing question',
      content: item.context_prompt,
      contentLabel: 'Context Prompt'
    });
  }

  // context_request arrives second (which data sources to fetch)
  if (item.context_request) {
    Object.entries(item.context_request)
      .filter(([, v]) => v)
      .forEach(([k]) => steps.push({ label: `Fetching ${formatKey(k)}…` }));
  }

  // full_prompt arrives last (the assembled prompt with all fetched context)
  if (item.full_prompt) {
    const label =
      item.context_prompt || item.context_request ? 'Context assembled' : 'Building context…';
    steps.push({ label, content: item.full_prompt, contentLabel: 'Full Prompt' });
  }

  return steps;
};

const ContextMessage = ({ item }: Props) => {
  const [expandedSteps, setExpandedSteps] = useState<Set<number>>(new Set());

  const isFetching = !item.full_prompt;
  const steps = buildSteps(item);

  const toggleStep = (i: number) => {
    setExpandedSteps((prev) => {
      const next = new Set(prev);
      next.has(i) ? next.delete(i) : next.add(i);
      return next;
    });
  };

  return (
    <div className="my-3 w-full text-gray-900 dark:text-gray-100">
      {/* Title */}
      <p className="font-bold text-base mb-3">{isFetching ? 'Thinking…' : ''}</p>

      {/* Timeline */}
      <div className="relative ml-1">
        <div className="absolute left-[6px] top-1.5 bottom-1.5 w-px bg-gray-200 dark:bg-gray-600" />
        <ul className="space-y-1">
          {steps.map((step, i) => {
            const isExpanded = expandedSteps.has(i);
            const hasContent = !!step.content;
            return (
              <li key={i}>
                <button
                  type="button"
                  onClick={() => hasContent && toggleStep(i)}
                  disabled={!hasContent}
                  className={`flex items-center gap-3 w-full text-left py-1 ${hasContent ? 'cursor-pointer group' : 'cursor-default'}`}>
                  <div className="relative z-10 shrink-0 w-3.5 flex items-center justify-center">
                    <div className="w-2.5 h-2.5 rounded-full bg-indigo-200 dark:bg-indigo-700" />
                  </div>
                  <span className="text-sm text-gray-500 dark:text-gray-400 grow">
                    {isFetching && step.label.endsWith('…') ? (
                      <>
                        {step.label.slice(0, -1)}
                        <AnimatedEllipsis />
                      </>
                    ) : (
                      step.label
                    )}
                  </span>
                  {hasContent && (
                    <FontAwesomeIcon
                      icon={isExpanded ? faChevronUp : faChevronDown}
                      size="xs"
                      className="text-gray-400 dark:text-gray-500 group-hover:text-indigo-500 transition-colors shrink-0"
                    />
                  )}
                </button>

                {isExpanded && step.content && (
                  <div className="ml-6 mt-1 mb-2 border-l-2 border-indigo-100 dark:border-indigo-900 pl-3">
                    {step.contentLabel && (
                      <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-1">
                        {step.contentLabel}
                      </p>
                    )}
                    <div className="prose dark:prose-invert prose-sm max-w-none">
                      <Markdown>{step.content}</Markdown>
                    </div>
                  </div>
                )}
              </li>
            );
          })}

          {isFetching && (
            <li className="flex items-center gap-3 py-1">
              <div className="w-3.5 shrink-0" />
              <span className="text-sm text-gray-400 dark:text-gray-500">
                <AnimatedEllipsis />
              </span>
            </li>
          )}
        </ul>
      </div>
    </div>
  );
};

export default React.memo(ContextMessage);
